'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { MultiValue } from 'react-select';

import { AppealStatus, GroupByOption, WriteOffCategory } from '@repo/types';
import { Checkbox, Icon, Input, Select, SelectOption } from '@repo/ui';
import { useDarkMode } from '@xtnd/web/shared/hooks/useDarkMode';

import { APPEAL_STATUS_OPTIONS, CATEGORY_OPTIONS, DAYS_PAST_DUE_OPTIONS, DEFAULT_APPEAL_STATUSES } from '../../constants';
import { useAppealStatusOptions, useFilterUpdate } from '../../hooks';
import { WriteOffGroupByOptions } from '../../types';
import { ArWriteOffFilter } from '../../types/writeOffs';

// ============================================================================
// Shared Components
// ============================================================================
interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  disabled?: boolean;
}

function SearchInput({
  value,
  onChange,
  placeholder = 'Search...',
  debounceMs = 1000,
  disabled = false,
}: SearchInputProps) {
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    const normalizedInput = inputValue.trim();
    const normalizedValue = value.trim();

    if (normalizedInput === normalizedValue) return;

    const timeoutId = setTimeout(() => {
      onChange(normalizedInput);
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [inputValue, value, onChange, debounceMs]);

  return (
    <div className="relative flex-1 duration-300 max-w-md">
      <Icon
        name="HiSearch"
        size="sm"
        className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
      />
      <Input
        value={inputValue}
        variant="outlined"
        onChange={e => setInputValue(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="flex w-full px-3 py-2 pl-9 border border-xtnd-light dark:border-xtnd-dark focus:ring-offset-xtnd-blue focus:ring-transparent focus:border-xtnd-blue dark:focus:border-xtnd-blue"
      />
    </div>
  );
}

interface AppealStatusSelectProps {
  value: SelectOption[];
  onChange: (selected: MultiValue<SelectOption> | null) => void;
  isLoading: boolean;
  disabled: boolean;
}

function AppealStatusSelect({ value, onChange, isLoading, disabled }: AppealStatusSelectProps) {
  const { isDark } = useDarkMode();
  const options = useAppealStatusOptions();

  return (
    <div className="w-fit min-w-[26rem] max-w-[26rem]">
      <Select<SelectOption>
        value={value}
        onChange={onChange}
        options={options}
        placeholder="Filter by appeal status..."
        multiple
        isLoading={isLoading}
        disabled={disabled}
        isDarkMode={isDark}
        classNamePrefix="react-select"
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        clearable
        searchable={false}
        menuPlacement="auto"
        menuPosition="fixed"
        fullWidth
      />
    </div>
  );
}

// ============================================================================
// WriteOffToolbar
// ============================================================================
type WriteOffToolbarProps = {
  isDataTableLoading: boolean;
  filters: ArWriteOffFilter;
  onChange: (next: ArWriteOffFilter) => void;
};

/**
 * Main toolbar for write-off list views.
 * Uses decoupled state for dropdown (statusSelected) and checkbox (isCheckedClosedInvoices)
 * to match the behavior in Appeals component.
 */
export function WriteOffToolbar({ isDataTableLoading, filters, onChange }: WriteOffToolbarProps) {
  const { search = '', groupBy, daysPastDue = 0, appealStatus, statusExplicit } = filters;
  const { update } = useFilterUpdate(filters, onChange);
  const [searchParams] = useSearchParams();
  const { isDark } = useDarkMode();

  // Helper to compute initial status selection from URL params
  const getInitialStatusSelected = (): SelectOption[] => {
    const filtersParam = searchParams.get('filters');
    if (!filtersParam) return [];

    // If status was explicitly set by user, restore it
    if (statusExplicit && appealStatus?.length) {
      const mode = isDark ? 'dark' : 'light';
      return appealStatus
        .map(status => APPEAL_STATUS_OPTIONS[mode][status as keyof typeof APPEAL_STATUS_OPTIONS.light])
        .filter(Boolean);
    }

    // If default statuses and not explicit, keep empty dropdown
    const isDefault = appealStatus?.length === DEFAULT_APPEAL_STATUSES.length &&
      appealStatus.every(s => DEFAULT_APPEAL_STATUSES.includes(s));
    if (!appealStatus || isDefault) return [];

    // Otherwise, map URL statuses to SelectOptions
    const mode = isDark ? 'dark' : 'light';
    return appealStatus
      .map(status => APPEAL_STATUS_OPTIONS[mode][status as keyof typeof APPEAL_STATUS_OPTIONS.light])
      .filter(Boolean);
  };

  // Decoupled state: dropdown shows user-selected statuses (excluding CLOSED)
  const [statusSelected, setStatusSelected] = useState<SelectOption[]>(getInitialStatusSelected);

  // Decoupled state: checkbox controls CLOSED inclusion separately
  const isInitialClosedChecked = (appealStatus ?? DEFAULT_APPEAL_STATUSES).includes(
    AppealStatus.CLOSED
  );
  const [isCheckedClosedInvoices, setIsCheckedClosedInvoices] = useState(isInitialClosedChecked);

  // Track initial mount to skip reset effect on first render
  const isFirstMount = useRef(true);

  // Reset local UI state when groupBy changes (skip initial mount)
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setStatusSelected([]);
    setIsCheckedClosedInvoices(false);
  }, [groupBy]);

  /**
   * Handle checkbox toggle - adds/removes CLOSED from API filter
   * When unchecking, also removes CLOSED from dropdown display
   */
  const handleCheckedClosedInvoices = useCallback(() => {
    const nextChecked = !isCheckedClosedInvoices;
    setIsCheckedClosedInvoices(nextChecked);

    // Get current status values from dropdown selection, or use non-closed defaults
    const currentStatusValues =
      statusSelected.length > 0
        ? statusSelected.map(s => s.value as AppealStatus)
        : DEFAULT_APPEAL_STATUSES;

    let nextStatus: AppealStatus[];
    if (nextChecked) {
      // Add CLOSED to current selection
      nextStatus = Array.from(new Set([...currentStatusValues, AppealStatus.CLOSED]));
    } else {
      // Remove CLOSED from current selection
      nextStatus = currentStatusValues.filter(s => s !== AppealStatus.CLOSED);
      if (nextStatus.length === 0) {
        nextStatus = DEFAULT_APPEAL_STATUSES;
      }

      // Also remove CLOSED from dropdown display
      setStatusSelected(prev => prev.filter(s => s.value !== AppealStatus.CLOSED));
    }

    update({ appealStatus: nextStatus, statusExplicit: true });
  }, [isCheckedClosedInvoices, statusSelected, update]);

  /**
   * Handle dropdown selection changes
   * Syncs checkbox if CLOSED is selected in dropdown
   */
  const handleSelectedStatus = useCallback(
    (selected: MultiValue<SelectOption> | null) => {
      const selectedOptions = (selected ?? []) as SelectOption[];
      setStatusSelected(selectedOptions);

      const statusValues = selectedOptions.map(s => s.value as AppealStatus);

      // Sync checkbox if CLOSED is selected in dropdown
      const includesClosed = statusValues.includes(AppealStatus.CLOSED);
      if (includesClosed) {
        setIsCheckedClosedInvoices(true);
      }

      // If empty selection (cleared via X), use defaults and reset explicit flag
      if (selectedOptions.length === 0) {
        const status = [...DEFAULT_APPEAL_STATUSES];
        if (isCheckedClosedInvoices) {
          status.push(AppealStatus.CLOSED);
        }
        update({ appealStatus: status, statusExplicit: false });
        return;
      }

      update({ appealStatus: statusValues.length > 0 ? statusValues : undefined, statusExplicit: true });
    },
    [isCheckedClosedInvoices, update]
  );

  const searchPlaceholder =
    groupBy === GroupByOption.CLIENT ? 'Search client' : 'Search matter, invoice, or client';

  return (
    <div className="flex justify-between items-center gap-4 w-full">
      <SearchInput
        value={search}
        onChange={value => update({ search: value })}
        placeholder={searchPlaceholder}
        disabled={isDataTableLoading}
      />

      <div className="flex gap-4 items-center">
        {groupBy === GroupByOption.NONE && (
          <AppealStatusSelect
            value={statusSelected}
            onChange={handleSelectedStatus}
            isLoading={isDataTableLoading}
            disabled={isDataTableLoading}
          />
        )}

        <Select
          className="w-[11rem] text-sm"
          options={WriteOffGroupByOptions}
          value={groupBy || ''}
          disabled={isDataTableLoading}
          onChange={option => {
            if (!option) return;
            const value = typeof option === 'string' ? option : option.value;
            update({ groupBy: value as ArWriteOffFilter['groupBy'] });
          }}
        />
        <Select
          className="w-[11rem] text-sm"
          options={DAYS_PAST_DUE_OPTIONS}
          value={`${daysPastDue}`}
          disabled={isDataTableLoading}
          onChange={option => {
            if (typeof option === 'string') {
              const value = parseInt(option, 10);
              update({ daysPastDue: isNaN(value) ? 30 : value });
            }
          }}
        />
        <label className="flex gap-x-2 justify-center items-center cursor-pointer">
          <Checkbox
            checked={isCheckedClosedInvoices}
            disabled={isDataTableLoading}
            onChange={handleCheckedClosedInvoices}
          />
          <span className="text-sm text-xtnd-dark dark:text-xtnd-light">
            Include Closed Appeals
          </span>
        </label>
      </div>
    </div>
  );
}

// ============================================================================
// ClientInvoiceToolbar
// ============================================================================
type ClientInvoiceToolbarProps = {
  isDataTableLoading: boolean;
  filters: ArWriteOffFilter;
  onChange: (next: ArWriteOffFilter) => void;
  /** Optional: Show a category filter for Approved tab drilldown */
  showCategoryFilter?: boolean;
};

/**
 * Toolbar for client detail drilldown view (when clicking a client row).
 * Uses decoupled state for dropdown (statusSelected) to match WriteOffToolbar behavior.
 * Shows: Search (Invoice Number / Matter Name), Appeal Status (local filter)
 * Optionally shows: Category filter (for ApprovedTab)
 */
export function ClientInvoiceToolbar({
  isDataTableLoading,
  filters,
  onChange,
  showCategoryFilter = false,
}: ClientInvoiceToolbarProps) {
  const { invoiceNumber = '', matterDisplayNumber = '', category, appealStatus, statusExplicit } = filters;
  const searchValue = invoiceNumber || matterDisplayNumber || '';
  const { update } = useFilterUpdate(filters, onChange);
  const [searchParams] = useSearchParams();
  const { isDark } = useDarkMode();

  // Helper to compute initial status selection from URL params
  const getInitialStatusSelected = (): SelectOption[] => {
    const filtersParam = searchParams.get('filters');
    if (!filtersParam) return [];

    // If status was explicitly set by user, restore it
    if (statusExplicit && appealStatus?.length) {
      const mode = isDark ? 'dark' : 'light';
      return appealStatus
        .map(status => APPEAL_STATUS_OPTIONS[mode][status as keyof typeof APPEAL_STATUS_OPTIONS.light])
        .filter(Boolean);
    }

    // If default statuses and not explicit, keep empty dropdown
    const isDefault = appealStatus?.length === DEFAULT_APPEAL_STATUSES.length &&
      appealStatus.every(s => DEFAULT_APPEAL_STATUSES.includes(s));
    if (!appealStatus || isDefault) return [];

    // Otherwise, map URL statuses to SelectOptions
    const mode = isDark ? 'dark' : 'light';
    return appealStatus
      .map(status => APPEAL_STATUS_OPTIONS[mode][status as keyof typeof APPEAL_STATUS_OPTIONS.light])
      .filter(Boolean);
  };

  // Decoupled state: dropdown shows user-selected statuses only
  const [statusSelected, setStatusSelected] = useState<SelectOption[]>(getInitialStatusSelected);

  /**
   * Handle dropdown selection changes
   */
  const handleSelectedStatus = useCallback(
    (selected: MultiValue<SelectOption> | null) => {
      const selectedOptions = (selected ?? []) as SelectOption[];
      setStatusSelected(selectedOptions);

      const statusValues = selectedOptions.map(s => s.value as AppealStatus);

      // If empty selection (cleared via X), use defaults and reset explicit flag
      if (selectedOptions.length === 0) {
        update({ appealStatus: DEFAULT_APPEAL_STATUSES, statusExplicit: false });
        return;
      }

      update({ appealStatus: statusValues.length > 0 ? statusValues : undefined, statusExplicit: true });
    },
    [update]
  );

  return (
    <div className="flex justify-between items-center w-full gap-4">
      <SearchInput
        value={searchValue}
        onChange={value => {
          update({
            invoiceNumber: value,
            matterDisplayNumber: value,
          });
        }}
        placeholder="Search matter or invoice"
        disabled={isDataTableLoading}
      />

      <div className="flex gap-4 items-center">
        <AppealStatusSelect
          value={statusSelected}
          onChange={handleSelectedStatus}
          isLoading={isDataTableLoading}
          disabled={isDataTableLoading}
        />

        {showCategoryFilter && (
          <Select
            className="w-[10rem] text-sm"
            options={CATEGORY_OPTIONS}
            value={category || ''}
            disabled={isDataTableLoading}
            onChange={option => {
              if (!option) {
                update({ category: undefined });
                return;
              }
              const value = typeof option === 'string' ? option : option.value;
              update({ category: (value as WriteOffCategory) || undefined });
            }}
          />
        )}
      </div>
    </div>
  );
}
